import { apiJson } from "../../../services/apiClient";
import { generateProjectName, generateStudyName } from "../../../services/api";
import { emptyOneshotFormData } from "./researchArtifacts";

function buildRequestId() {
  const cryptoApi =
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
      ? window.crypto
      : undefined;
  if (cryptoApi) return cryptoApi.randomUUID();
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Bootstrap a plan via the existing create-plan-oneshot endpoint.
 * UI may only collect a goal; remaining form fields stay empty/default.
 */
export async function createPlanOneshotBootstrap({
  problemDefinition,
  studyName = "",
  projectId = null,
  createProject,
  canCreateProject,
}) {
  const goal = String(problemDefinition || "").trim();
  if (goal.length < 10) {
    return { success: false, error: "목표는 최소 10자 이상 입력해주세요." };
  }

  let targetProjectId = projectId;
  let finalStudyName = String(studyName || "").trim();

  if (!targetProjectId) {
    if (typeof canCreateProject === "function" && !canCreateProject()) {
      return { success: false, error: "quota_project", upgradeReason: "project" };
    }
    const projectNameResult = await generateProjectName(finalStudyName || "새 리서치", goal);
    if (!projectNameResult?.success) {
      return { success: false, error: "프로젝트명 생성에 실패했습니다." };
    }
    const createdProject = await createProject({
      name: projectNameResult.projectName,
      domain: "general",
      tags: projectNameResult.tags || [],
    });
    if (!createdProject?.id) {
      return { success: false, error: "프로젝트 생성에 실패했습니다." };
    }
    targetProjectId = createdProject.id;
  }

  if (!finalStudyName) {
    try {
      const studyNameResult = await generateStudyName(goal);
      finalStudyName =
        studyNameResult?.success && studyNameResult.studyName
          ? studyNameResult.studyName
          : `리서치-${Date.now()}`;
    } catch {
      finalStudyName = `리서치-${Date.now()}`;
    }
  }

  const formData = emptyOneshotFormData(goal, finalStudyName);
  const result = await apiJson("/generator/create-plan-oneshot", {
    method: "POST",
    body: JSON.stringify({
      formData,
      projectId: targetProjectId,
      requestId: buildRequestId(),
    }),
  });

  if (result?.success && (result.study_slug || result.study_id)) {
    return {
      success: true,
      studySlug: result.study_slug || result.study_id,
      studyId: result.study_id,
      artifactId: result.artifact_id,
    };
  }

  return {
    success: false,
    error: result?.error || "계획서 생성에 실패했습니다.",
  };
}

/**
 * Create a screener or guideline directly from user-provided context.
 * Unlike the legacy bootstrap, this intentionally does not create a plan artifact.
 */
export async function createDirectArtifactBootstrap({
  artifactKind,
  sourceText,
  sourceType = "quick_goal",
  methodologies = [],
  studyName = "",
  projectId = null,
  createProject,
  canCreateProject,
}) {
  const context = String(sourceText || "").trim();
  if (context.length < 10) {
    return { success: false, error: "생성 근거는 최소 10자 이상 입력해주세요." };
  }

  let targetProjectId = projectId;
  let finalStudyName = String(studyName || "").trim();

  if (!targetProjectId) {
    if (typeof canCreateProject === "function" && !canCreateProject()) {
      return { success: false, error: "quota_project", upgradeReason: "project" };
    }
    const projectNameResult = await generateProjectName(finalStudyName || "새 리서치", context);
    if (!projectNameResult?.success) {
      return { success: false, error: "프로젝트명 생성에 실패했습니다." };
    }
    const createdProject = await createProject({
      name: projectNameResult.projectName,
      domain: "general",
      tags: projectNameResult.tags || [],
    });
    if (!createdProject?.id) {
      return { success: false, error: "프로젝트 생성에 실패했습니다." };
    }
    targetProjectId = createdProject.id;
  }

  if (!finalStudyName) {
    try {
      const studyNameResult = await generateStudyName(context);
      finalStudyName =
        studyNameResult?.success && studyNameResult.studyName
          ? studyNameResult.studyName
          : `리서치-${Date.now()}`;
    } catch {
      finalStudyName = `리서치-${Date.now()}`;
    }
  }

  const result = await apiJson("/generator/create-direct-artifact", {
    method: "POST",
    body: JSON.stringify({
      artifactKind,
      sourceText: context,
      sourceType,
      methodologies,
      studyName: finalStudyName,
      projectId: targetProjectId,
    }),
  });

  if (result?.success && (result.study_slug || result.study_id)) {
    return {
      success: true,
      studySlug: result.study_slug || result.study_id,
      studyId: result.study_id,
      artifactId: result.artifact_id,
    };
  }

  return {
    success: false,
    error: result?.error || "산출물 생성에 실패했습니다.",
  };
}
